// Decision 185: once the owner has the app writing its invoices to QuickBooks, an invoice made here is real (it goes to
// QuickBooks as the CPA's copy). Where invoices are made, the band says which it is; on an invoice, where its copy is.
import type { Invoice } from '@morales/shared';
import { useQuery } from '@tanstack/react-query';
import { api } from '../api';
import { useAuth } from '../auth';
import { whenAt } from '../format';
import { useLocale } from '../i18n';
import { qk } from '../queryClient';
import { PracticeBanner } from './ui';

/** Whether an invoice made now is real and goes to QuickBooks (owner and office read it; anyone else sees practice). */
export function useInvoicesAreReal(): boolean | undefined {
  const { me } = useAuth();
  const books = me?.role === 'owner' || me?.role === 'office';
  const q = useQuery({ queryKey: qk.qboWrite, queryFn: ({ signal }) => api.qboWrite(signal), enabled: books, staleTime: 60_000 });
  if (!books) return false;
  return q.data?.real;
}

/** The switch day has come (decision 186): money is recorded in the app on every invoice, QuickBooks' older ones too. */
export function useBooksSwitched(): boolean {
  const { me } = useAuth();
  const books = me?.role === 'owner' || me?.role === 'office';
  const q = useQuery({ queryKey: qk.qboWrite, queryFn: ({ signal }) => api.qboWrite(signal), enabled: books, staleTime: 60_000 });
  return books && !!q.data?.switchedAt;
}

/** QuickBooks holds the books (the money mirror on, before the switch): real money is entered there, practice here. */
export function useQuickBooksHolds(): boolean {
  const { me } = useAuth();
  const books = me?.role === 'owner' || me?.role === 'office';
  const q = useQuery({ queryKey: qk.qboWrite, queryFn: ({ signal }) => api.qboWrite(signal), enabled: books, staleTime: 60_000 });
  return books && !!q.data?.mirrorOn && !q.data?.switchedAt;
}

/** Where invoices are made: "real, copied to QuickBooks" while writing is on; the practice band otherwise. */
export function InvoiceMakingBand() {
  const { t } = useLocale();
  const real = useInvoicesAreReal();
  if (real === undefined) return null;
  if (!real) return <PracticeBanner />;
  return (
    <p className="ok-band no-print" role="note" data-testid="real-invoices-band">
      <strong>{t('qboRealTitle')}</strong> {t('qboRealHint')}
    </p>
  );
}

/** On an invoice made in the app while writing is on: waiting, sent as #N, or refused (and why). */
export function QboWriteChip({ invoice }: { invoice: Pick<Invoice, 'quickbooks'> }) {
  const { t, intlLocale } = useLocale();
  const w = invoice.quickbooks;
  if (!w) return null;
  if (w.state === 'sent')
    return (
      <p className="note no-print" data-testid="qbo-write-chip" data-state="sent">
        ✓ {t('qboWriteSent').replace('{n}', w.docNumber ?? '—')}
        {w.at ? ` · ${whenAt(w.at, intlLocale)}` : ''}
      </p>
    );
  if (w.state === 'failed')
    return (
      <p className="warn-band no-print" data-testid="qbo-write-chip" data-state="failed">
        {t('qboWriteFailed')} {w.error ?? ''}
      </p>
    );
  return (
    <p className="muted no-print" data-testid="qbo-write-chip" data-state="waiting">
      {t('qboWriteWaiting')}
    </p>
  );
}
